import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  showBackButton,
  onBackButtonClick,
  offBackButtonClick,
  hideBackButton,
  mountBackButton,
  unmountBackButton,
} from '@telegram-apps/sdk-react';
import { BottomNav } from '../../components/design/BottomNav';
import { InlineCTA } from '../../components/design/InlineCTA';
import { StepIndicator } from '../../components/design/StepIndicator';
import { SceneCarousel } from '../../components/design/SceneCarousel';
import { ExamplesSlider } from '../../components/design/ExamplesSlider';
import { brand, fontFamily, tg } from '../../theme/tokens';
import { SCENES } from '../../data/scenes';
import { useWizard } from '../../state/wizard';

/**
 * Step 2 — pick a scene (v2 layout).
 *
 * Replaces the previous vertical-card grid with a horizontally-swipable
 * carousel + a "preview examples for the selected scene" slider below.
 * Rationale: in the grid version users couldn't tell what each scene
 * actually looked like beyond the gradient placeholder; the examples
 * slider gives concrete reference frames.
 *
 * Carousel selects index 0 (Sunset Beach) on mount so the CTA + examples
 * section are always populated — matches mockup. User can swipe or tap
 * peek cards / pagination dots to change selection.
 */
export function SceneStep() {
  const navigate = useNavigate();
  const { photo1, photo2, scene: selectedScene, setScene } = useWizard();

  // Guard — without photos there's nothing to schedule, bounce home.
  useEffect(() => {
    if (!photo1 || !photo2) navigate('/', { replace: true });
  }, [photo1, photo2, navigate]);

  // TG native BackButton (not MainButton — we use InlineCTA instead).
  useEffect(() => {
    try { mountBackButton(); showBackButton(); } catch { /* */ }
    const onBack = () => navigate('/', { replace: false });
    onBackButtonClick(onBack);
    return () => {
      offBackButtonClick(onBack);
      try { hideBackButton(); unmountBackButton(); } catch { /* */ }
    };
  }, [navigate]);

  const activeScene = selectedScene
    ? SCENES.find((s) => s.id === selectedScene) ?? SCENES[0]
    : SCENES[0];

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', paddingBottom: 180 }}>
      <StepIndicator step={2} total={4} />

      <div style={{ padding: '18px 20px 4px', fontFamily }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: tg.text, letterSpacing: -0.5 }}>
          Pick a scene
        </h1>
        <div style={{ fontSize: 15, color: tg.textHint, marginTop: 4, lineHeight: 1.4 }}>
          Swipe through scenes — preview real examples below
        </div>
      </div>

      <SceneCarousel
        scenes={SCENES}
        selectedId={selectedScene}
        onSelect={setScene}
      />

      {/* Examples for the active scene. `key` resets internal slider scroll
          position back to slide 0 each time the scene changes — otherwise
          users see the previous scene's slider stuck mid-scroll. */}
      <div style={{ padding: '18px 20px 0', fontFamily }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: tg.text }}>
            Examples from{' '}
            <span style={{ color: brand.solid }}>{activeScene.name}</span>
          </div>
          <div style={{ fontSize: 11, color: tg.textHint }}>
            {activeScene.examples.length} previews
          </div>
        </div>
        <ExamplesSlider key={activeScene.id} items={activeScene.examples} />
      </div>

      <div style={{ flex: 1 }} />

      {/* CTA always visible — carousel auto-selects on mount so there's
          always an active scene. Label tells user exactly what they're
          continuing with, reinforcing the carousel selection. */}
      <InlineCTA onClick={() => navigate('/wizard/type')}>
        Continue with {activeScene.name}
      </InlineCTA>

      <BottomNav />
    </div>
  );
}
